import { useState, useEffect, useCallback, useRef } from 'react';

export interface SelectOption {
  id: number | string;
  label: string;
}

interface UseSelectInputProps<T extends SelectOption> {
  loadOptions: (query: string) => Promise<T[]>;
  debounceMs?: number;
  minQueryLength?: number;
}

export const useSelectInput = <T extends SelectOption>({
  loadOptions,
  debounceMs = 300,
  minQueryLength = 1,
}: UseSelectInputProps<T>) => {
  const [inputValue, setInputValueState] = useState('');
  const [selectedOption, setSelectedOption] = useState<T | null>(null);
  const [options, setOptions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const fetchOptions = useCallback(
    async (query: string) => {
      if (!isMountedRef.current) {
        return;
      }

      const currentRequestId = ++requestIdRef.current;

      try {
        const results = await loadOptions(query);

        if (!isMountedRef.current || currentRequestId !== requestIdRef.current) {
          return;
        }

        setOptions(results);
        setIsOpen(true);
      } catch (error) {
        console.error('Ошибка загрузки опций:', error);

        if (!isMountedRef.current || currentRequestId !== requestIdRef.current) {
          return;
        }

        setOptions([]);
        setIsOpen(true);
      } finally {
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [loadOptions]
  );

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValueState(value);
      setSelectedOption(null);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (value.length >= minQueryLength) {
        setIsLoading(true);
        setIsOpen(true);
        setOptions([]);

        timeoutRef.current = setTimeout(() => {
          fetchOptions(value);
        }, debounceMs);
      } else {
        requestIdRef.current += 1;
        setOptions([]);
        setIsOpen(false);
        setIsLoading(false);
      }
    },
    [debounceMs, fetchOptions, minQueryLength]
  );

  const loadCurrentOptions = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (inputValue.length >= minQueryLength) {
      setIsLoading(true);
      setIsOpen(true);
      setOptions([]);

      fetchOptions(inputValue);
    } else {
      requestIdRef.current += 1;
      setOptions([]);
      setIsOpen(false);
      setIsLoading(false);
    }
  }, [fetchOptions, inputValue, minQueryLength]);

  const handleSelect = useCallback((option: T) => {
    requestIdRef.current += 1;
    setSelectedOption(option);
    setInputValueState(option.label);
    setIsOpen(false);
    setIsLoading(false);
  }, []);

  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    requestIdRef.current += 1;
    setInputValueState('');
    setSelectedOption(null);
    setOptions([]);
    setIsOpen(false);
    setIsLoading(false);
  }, []);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (isMountedRef.current) {
        setIsOpen(false);
      }
    }, 150);
  }, []);

  const openDropdown = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }

    setIsOpen(true);
  }, []);

  const closeDropdown = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }

    setIsOpen(false);
  }, []);

  return {
    inputValue,
    setInputValue: handleInputChange,
    selectedOption,
    options,
    isLoading,
    isOpen,
    handleSelect,
    reset,
    handleBlur,
    openDropdown,
    closeDropdown,
    loadCurrentOptions,
  };
};